import React, { useCallback, useEffect, useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from './ui/Button';
const slides = [{
  id: 0,
  bg: 'https://cdn.magicpatterns.com/uploads/tThsK6jChgFFgaHRejZeRc/pexels-nappy-936012.jpg',
  brand: "Beni's African Braiding",
  headline: 'A Premium Natural Hair Salon In Waldorf, MD',
  subtitle: "We give you the premium salon experience you've been looking for — offering comprehensive braiding services!",
  ctaText: 'Book Appointment',
  ctaAction: '/booking',
  isPhone: false
}, {
  id: 1,
  bg: 'https://cdn.magicpatterns.com/uploads/uWvNpm55K2t36oVwidKUKL/pexels-natalia-garcia-prieto-62947227-10810251.jpg',
  brand: 'Expert Protective Styles',
  headline: 'Braids, Locs, Twists & Cornrows',
  subtitle: 'From knotless braids to goddess locs, our master stylists craft every style with precision and care.',
  ctaText: 'Explore Services',
  ctaAction: '/services',
  isPhone: false
}, {
  id: 2,
  bg: 'https://cdn.magicpatterns.com/uploads/uKLsANdv6QKjhNf51bjrTv/pexels-click-pop-2298747-4566919.jpg',
  brand: 'Crown Your Beauty',
  headline: 'Walk-ins Welcome, No Appointment Needed',
  subtitle: 'Visit us at 2768 Crain Hwy, Waldorf, MD 20601. Call now to book your next look.',
  ctaText: 'Call Now — 301-385-8034',
  ctaAction: 'tel:3013858034',
  isPhone: true
}];
// --- Background Animation Components ---
const GRID_ROWS = 5;
const GRID_COLS = 5;
function GridBackground({
  src
}: {
  src: string;
}) {
  return <div className="absolute inset-0 w-full h-full" data-id="element-186">
      {/* Full background image */}
      <div className="absolute inset-0 w-full h-full bg-cover bg-center" style={{
      backgroundImage: `url(${src})`
    }} data-id="element-187" />
      {/* Animated grid overlay that reveals the image */}
      <div className="absolute inset-0 w-full h-full flex flex-col" data-id="element-188">
        {Array.from({
        length: GRID_ROWS
      }).map((_, row) => <div key={row} className="flex-1 flex" data-id="element-189">
            {Array.from({
          length: GRID_COLS
        }).map((_, col) => {
          const delay = (row + col) * 0.06;
          return <motion.div key={col} initial={{
            opacity: 1
          }} animate={{
            opacity: 0
          }} transition={{
            duration: 0.5,
            delay: delay,
            ease: 'easeOut'
          }} className="flex-1 h-full bg-jet" data-id="element-190" />;
        })}
          </div>)}
      </div>
    </div>;
}
const BLINDS_ROWS = 8;
function BlindsBackground({
  src
}: {
  src: string;
}) {
  return <div className="absolute inset-0 w-full h-full" data-id="element-191">
      {/* Full background image */}
      <div className="absolute inset-0 w-full h-full bg-cover bg-center" style={{
      backgroundImage: `url(${src})`
    }} data-id="element-192" />
      {/* Animated blinds overlay that reveals the image */}
      <div className="absolute inset-0 w-full h-full flex flex-col" data-id="element-193">
        {Array.from({
        length: BLINDS_ROWS
      }).map((_, row) => {
        const delay = row * 0.1;
        return <motion.div key={row} initial={{
          scaleY: 1
        }} animate={{
          scaleY: 0
        }} transition={{
          duration: 0.6,
          delay: delay,
          ease: 'easeOut'
        }} className="flex-1 w-full origin-top bg-jet" data-id="element-194" />;
      })}
      </div>
    </div>;
}
function ZoomBackground({
  src
}: {
  src: string;
}) {
  return <motion.div initial={{
    scale: 1.3,
    opacity: 0
  }} animate={{
    scale: 1,
    opacity: 1
  }} transition={{
    duration: 1.2,
    ease: 'easeOut'
  }} className="absolute inset-0 w-full h-full bg-cover bg-center" style={{
    backgroundImage: `url(${src})`
  }} data-id="element-195" />;
}
// --- Text Animation Variants ---
const textAnimations = [
// Slide 0: Slide up
{
  container: {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.3
      }
    }
  },
  item: {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  }
},
// Slide 1: Slide from right
{
  container: {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.3
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.3
      }
    }
  },
  item: {
    hidden: {
      opacity: 0,
      x: 60
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  }
},
// Slide 2: Scale up
{
  container: {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.15
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.3
      }
    }
  },
  item: {
    hidden: {
      opacity: 0,
      scale: 0.85
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  }
}];
export function HeroSection() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % slides.length);
    setResetKey(k => k + 1);
  }, []);
  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + slides.length) % slides.length);
    setResetKey(k => k + 1);
  }, []);
  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
    setResetKey(k => k + 1);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [resetKey]);
  const currentSlide = slides[currentIndex];
  const currentTextAnimation = textAnimations[currentIndex];
  const renderBackground = () => {
    switch (currentIndex) {
      case 0:
        return <GridBackground src={currentSlide.bg} data-id="element-196" />;
      case 1:
        return <BlindsBackground src={currentSlide.bg} data-id="element-197" />;
      case 2:
        return <ZoomBackground src={currentSlide.bg} data-id="element-198" />;
      default:
        return <GridBackground src={currentSlide.bg} data-id="element-199" />;
    }
  };
  return <section className="relative min-h-[75vh] md:min-h-screen flex items-end pb-16 md:items-center md:pb-0 overflow-hidden bg-jet pt-20" data-id="element-200">
      {/* Background Images with Reveal Effect */}
      <div className="absolute inset-0 z-0" data-id="element-201">
        <AnimatePresence mode="popLayout" data-id="element-202">
          <motion.div key={currentIndex} className="absolute inset-0 z-0"
        // Hold the old image visible while the new one animates in on top
        exit={{
          opacity: 1,
          transition: {
            duration: 1.5
          }
        }} data-id="element-203">
            {renderBackground()}
          </motion.div>
        </AnimatePresence>

        {/* Gradient Overlay: Bottom-up on mobile for image visibility, left-right on desktop for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-jet/95 via-jet/50 to-jet/20 md:bg-gradient-to-r md:from-jet/90 md:via-jet/60 md:to-jet/20 z-10 pointer-events-none" data-id="element-204"></div>
      </div>

      {/* Content Container */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-205">
        <AnimatePresence mode="wait" data-id="element-206">
          <motion.div key={`content-${currentIndex}-${resetKey}`} className="max-w-2xl text-left" variants={currentTextAnimation.container} initial="hidden" animate="visible" exit="exit" data-id="element-207">
            <motion.h2 variants={currentTextAnimation.item} className="font-heading italic text-3xl md:text-4xl text-white mb-4" data-id="element-208">
              {currentSlide.brand}
            </motion.h2>
            <motion.h1 variants={currentTextAnimation.item} className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-6" data-id="element-209">
              {currentSlide.headline}
            </motion.h1>
            <motion.p variants={currentTextAnimation.item} className="font-body text-lg md:text-xl text-white/90 mb-8 leading-relaxed" data-id="element-210">
              {currentSlide.subtitle}
            </motion.p>
            <motion.div variants={currentTextAnimation.item} data-id="element-211">
              {currentSlide.isPhone ? <a href={currentSlide.ctaAction} data-id="element-212">
                  <Button variant="primary" size="lg" className="bg-golden text-jet hover:bg-gold-muted glow-gold" data-id="element-213">
                    {currentSlide.ctaText}
                  </Button>
                </a> : <Button variant="primary" size="lg" className="bg-golden text-jet hover:bg-gold-muted glow-gold" onClick={() => navigate(currentSlide.ctaAction)} data-id="element-214">
                  {currentSlide.ctaText}
                </Button>}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Arrows */}
      <button onClick={goToPrev} aria-label="Previous slide" className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-jet/40 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:bg-jet/60 transition-all" data-id="element-215">
        <ChevronLeftIcon className="w-6 h-6" data-id="element-216" />
      </button>
      <button onClick={goToNext} aria-label="Next slide" className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-jet/40 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:bg-jet/60 transition-all" data-id="element-217">
        <ChevronRightIcon className="w-6 h-6" data-id="element-218" />
      </button>

      {/* Dot Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-3" data-id="element-219">
        {slides.map((_, idx) => <button key={idx} onClick={() => goToSlide(idx)} aria-label={`Go to slide ${idx + 1}`} className={`w-3 h-3 rounded-full transition-all duration-300 ${idx === currentIndex ? 'bg-golden scale-125' : 'bg-white/30 hover:bg-white/50'}`} data-id="element-220" />)}
      </div>
    </section>;
}